/**
 * Pure helper layer for the request-flow DBQ editor.
 * Centralizes inline suggestion state, field targeting/navigation helpers, missing-detail
 * handling, evidence transforms, and musculoskeletal supplemental utilities.
 */
import {
  getMusculoskeletalSupplementalFieldValue,
  isMusculoskeletalSupplementalFieldId,
  MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_DEFINITIONS,
  normalizeMusculoskeletalSupplementalData,
} from '../dbqMusculoskeletalConstants';

const normalizeDbqTextForCompare = (value) => {
  return String(value || '')
    .toLowerCase()
    .replace(/[’]/g, "'")
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
};

const isExaminerCertificationAndSignatureSection = (section) => {
  const label = normalizeDbqTextForCompare(section?.label);

  // Be intentionally fuzzy: different DBQs sometimes vary punctuation/case.
  return label.includes('examiner') && label.includes('certification') && label.includes('signature');
};

const findRemarksFieldIdInElements = (elements = []) => {
  for (const el of elements) {
    if (el?.type === 'field' && el?.id) {
      const idLower = String(el.id).toLowerCase();
      const labelLower = String(el.label || '').toLowerCase();
      if (
        idLower.includes('remarks') ||
        idLower.includes('remark') ||
        labelLower.includes('remarks')
      ) {
        return el.id;
      }
    }

    if (el?.elements?.length) {
      const nested = findRemarksFieldIdInElements(el.elements);
      if (nested) return nested;
    }

    if (el?.options?.length) {
      for (const opt of el.options) {
        const nested = findRemarksFieldIdInElements(opt.child_elements || []);
        if (nested) return nested;
      }
    }
  }

  return null;
};

const parseStoredRequestContentions = (value) => String(value || '')
  .split('\n')
  .map((entry) => String(entry || '').trim())
  .filter(Boolean);

const getContentionDisplayLabel = (value) => String(value || '').split(' > ').slice(-1)[0] || value;

const getMoToeLabel = (toe = null, toeId = null) => {
  if (toe && typeof toe === 'object') {
    return toe.title || toe.toeType || toe.toeId || toeId || 'Not selected';
  }
  return toeId || 'Not selected';
};

const buildContentionOpinionHeader = (contentionOpinion = {}) => {
  const contentionLabel =
    contentionOpinion?.contentionLabel || contentionOpinion?.contention || 'Contention';
  const toeLabel =
    contentionOpinion?.toe?.title ||
    contentionOpinion?.toe?.toeType ||
    contentionOpinion?.toe?.toeId ||
    contentionOpinion?.toeId ||
    '';

  return [`Contention: ${contentionLabel}`, toeLabel ? `Theory of Entitlement: ${toeLabel}` : null]
    .filter(Boolean)
    .join('\n');
};

const buildCompiledContentionOpinionText = (contentionOpinions = []) => {
  if (!Array.isArray(contentionOpinions) || contentionOpinions.length === 0) {
    return '';
  }

  return contentionOpinions
    .map((contentionOpinion) => {
      const body =
        String(contentionOpinion?.cleanedText || contentionOpinion?.rawText || '').trim();
      return [buildContentionOpinionHeader(contentionOpinion), body]
        .filter(Boolean)
        .join('\n\n');
    })
    .filter(Boolean)
    .join('\n\n')
    .trim();
};

const getEditableAutoMedicalOpinionText = (autoMedicalOpinion = null) => {
  if (
    !autoMedicalOpinion ||
    typeof autoMedicalOpinion !== 'object' ||
    Array.isArray(autoMedicalOpinion)
  ) {
    return '';
  }

  const candidates = [
    autoMedicalOpinion.remarksOverrideText,
    autoMedicalOpinion.fullText,
    autoMedicalOpinion.cleanedText,
    autoMedicalOpinion.rawText,
    buildCompiledContentionOpinionText(autoMedicalOpinion.contentionOpinions),
  ];

  return candidates
    .map((value) => String(value || '').trim())
    .find(Boolean) || '';
};

const EMPTY_SUGGESTION_STATS = Object.freeze({
  total: 0,
  pending: 0,
  accepted: 0,
  rejected: 0,
  dismissed: 0,
});

const SUGGESTION_STATUS_FILTERS = Object.freeze({
  pending: 'pending',
  accepted: 'accepted',
  rejected: 'rejected',
  dismissed: 'dismissed',
});

const SUGGESTION_STATUS_FILTER_ORDER = [
  SUGGESTION_STATUS_FILTERS.pending,
  SUGGESTION_STATUS_FILTERS.accepted,
  SUGGESTION_STATUS_FILTERS.rejected,
  SUGGESTION_STATUS_FILTERS.dismissed,
];

const INLINE_SUGGESTION_VIEW_MODES = Object.freeze({
  suggestions: 'suggestions',
  currentDbq: 'current_dbq',
});

const MISSING_USER_INPUT_TOKEN = '[MISSING – FLAG FOR USER]';

const INLINE_SUGGESTION_NAVIGATION_STATUSES = new Set([
  SUGGESTION_STATUS_FILTERS.pending,
  SUGGESTION_STATUS_FILTERS.accepted,
  SUGGESTION_STATUS_FILTERS.rejected,
]);

const ACTIVE_DBQ_AI_GENERATION_STATUSES = new Set([
  'pending',
  'processing',
  'queued',
  'in_progress',
  'running',
]);

const isActiveDbqAiGenerationStatus = (status) =>
  ACTIVE_DBQ_AI_GENERATION_STATUSES.has(String(status || '').trim().toLowerCase());

const hasActiveDbqAiGeneration = (formData = {}) => {
  if (!formData || typeof formData !== 'object') return false;

  if (isActiveDbqAiGenerationStatus(formData?._autoMedicalOpinion?.status)) {
    return true;
  }

  const aiGenerationBucket = formData?._aiFieldGeneration;
  if (!aiGenerationBucket || typeof aiGenerationBucket !== 'object' || Array.isArray(aiGenerationBucket)) {
    return false;
  }

  return Object.values(aiGenerationBucket).some((state) =>
    state &&
    typeof state === 'object' &&
    !Array.isArray(state) &&
    isActiveDbqAiGenerationStatus(state.status));
};

const getUserDbqAiPollingInterval = (queryData) =>
  hasActiveDbqAiGeneration(queryData?.userDbq?.formData) ? 3000 : false;

const AI_DRAFT_LABELS = Object.freeze({
  medical_opinion: 'Medical Opinion',
  evidence_review: 'Evidence Review',
  medical_history: 'Medical History',
  functional_impact: 'Functional Impact',
  diagnostic_testing_results: 'Diagnostic Testing Results',
  diagnostic_testing_other_findings: 'Diagnostic Testing Other Findings',
  musculoskeletal_flareups: 'Flare-Ups',
  musculoskeletal_functional_loss: 'Functional Loss',
});

const buildSuggestionStatsFromItems = (items = []) => items.reduce((stats, item) => {
  const status = String(item?.status || SUGGESTION_STATUS_FILTERS.pending).toLowerCase();

  stats.total += 1;
  if (status === SUGGESTION_STATUS_FILTERS.accepted) stats.accepted += 1;
  else if (status === SUGGESTION_STATUS_FILTERS.rejected) stats.rejected += 1;
  else if (status === SUGGESTION_STATUS_FILTERS.dismissed) stats.dismissed += 1;
  else stats.pending += 1;

  return stats;
}, {
  total: 0,
  pending: 0,
  accepted: 0,
  rejected: 0,
  dismissed: 0,
});

const buildAiReviewNotice = ({
  fieldId,
  draftType,
  title,
  message,
  detail = null,
  createdAt = null,
  tone = 'info',
  navigateToFieldId = null,
} = {}) => {
  const normalizedFieldId = String(fieldId || '').trim();
  if (!normalizedFieldId || !title) return null;

  const label = AI_DRAFT_LABELS[draftType] || draftType || 'AI';
  const timestamp = createdAt || new Date().toISOString();

  return {
    suggestionId: `ai-notice:${draftType || 'unknown'}:${tone}:${normalizedFieldId}:${timestamp}`,
    fieldId: normalizedFieldId,
    fieldLabel: label,
    fieldType: 'ai_notice',
    payload: { value: message || title },
    appliedValue: null,
    summary: title,
    note: detail ? String(detail).trim() : null,
    status: SUGGESTION_STATUS_FILTERS.pending,
    createdAt: timestamp,
    createdBy: {
      userId: null,
      name: `${label} AI`,
    },
    source: 'ai_notice',
    aiNotice: {
      draftType: draftType || null,
      tone,
      title,
      message: message || '',
      navigateToFieldId: String(navigateToFieldId || '').trim() || null,
    },
  };
};

const normalizeDbqFieldType = (field = {}) => String(field?.field_type || '').trim().toLowerCase();

const AI_FIELD_TYPE_SETS = Object.freeze({
  text: new Set(['short_text', 'long_text']),
  longText: new Set(['long_text']),
  musculoskeletal: new Set(['yes_no', 'checkbox', 'short_text', 'long_text']),
});

const normalizeAiFieldMatchText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/__/g, ' ')
    .replace(/[._-]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

const aiMatchIncludesAllTerms = (text, terms = []) => {
  if (!text || !Array.isArray(terms) || terms.length === 0) return false;
  return terms.every((term) => text.includes(normalizeAiFieldMatchText(term)));
};

const aiMatchIncludesAnyTermSet = (text, termSets = []) =>
  termSets.some((terms) => aiMatchIncludesAllTerms(text, terms));

const buildAiFieldSearchIndex = ({
  field = {},
  section = {},
  breadcrumbs = [],
} = {}) => {
  const fieldLabel = normalizeAiFieldMatchText(field?.label);
  const fieldId = normalizeAiFieldMatchText(field?.id);
  const sectionLabel = normalizeAiFieldMatchText(section?.label);
  const sectionId = normalizeAiFieldMatchText(section?.id);
  const breadcrumbText = breadcrumbs.map(normalizeAiFieldMatchText).filter(Boolean).join(' ');
  const fullText = [
    fieldLabel,
    fieldId,
    sectionLabel,
    sectionId,
    breadcrumbText,
  ].filter(Boolean).join(' ');

  return {
    fieldLabel,
    fieldId,
    sectionLabel,
    sectionId,
    breadcrumbs: breadcrumbText,
    fullText,
  };
};

const collectDbqAiFieldContexts = (formJson = {}) => {
  if (!Array.isArray(formJson?.sections)) return [];

  const contexts = [];
  let sourceOrder = 0;

  const visitElements = (elements = [], section = {}, breadcrumbs = []) => {
    if (!Array.isArray(elements)) return;

    elements.forEach((element) => {
      if (!element || typeof element !== 'object') return;

      const elementLabel = String(element?.label || element?.text || element?.id || '').trim();
      const nextBreadcrumbs = elementLabel ? [...breadcrumbs, elementLabel] : breadcrumbs;

      if (element.type === 'field' && element.id) {
        contexts.push({
          field: element,
          fieldId: String(element.id),
          fieldType: normalizeDbqFieldType(element),
          sectionId: String(section?.id || ''),
          sectionLabel: String(section?.label || ''),
          sectionIndex: section?.sectionIndex ?? -1,
          sourceOrder: sourceOrder++,
          search: buildAiFieldSearchIndex({
            field: element,
            section,
            breadcrumbs,
          }),
        });
      }

      visitElements(element.elements || [], section, nextBreadcrumbs);

      (element.options || []).forEach((option) => {
        const optionLabel = String(option?.label || option?.value || option?.id || '').trim();
        visitElements(
          option?.child_elements || [],
          section,
          optionLabel ? [...nextBreadcrumbs, optionLabel] : nextBreadcrumbs,
        );
      });
    });
  };

  formJson.sections.forEach((section, sectionIndex) => {
    visitElements(
      section?.elements || [],
      {
        ...section,
        sectionIndex,
      },
      [],
    );
  });

  return contexts;
};

const scoreDbqAiFieldContext = ({
  context,
  patterns = [],
  excludedPatterns = [],
  allowedFieldTypes = null,
} = {}) => {
  if (!context?.fieldId) return null;
  if (allowedFieldTypes && !allowedFieldTypes.has(context.fieldType)) return null;
  if (
    excludedPatterns.some((terms) =>
      aiMatchIncludesAllTerms(context.search.fullText, terms))
  ) {
    return null;
  }

  let score = 0;
  for (const terms of patterns) {
    if (aiMatchIncludesAllTerms(context.search.fieldLabel, terms)) score += 80;
    else if (aiMatchIncludesAllTerms(context.search.breadcrumbs, terms)) score += 45;
    else if (aiMatchIncludesAllTerms(context.search.sectionLabel, terms)) score += 30;
    else if (aiMatchIncludesAllTerms(context.search.fieldId, terms)) score += 20;
    else if (aiMatchIncludesAllTerms(context.search.fullText, terms)) score += 10;
  }

  if (score <= 0) return null;

  return {
    ...context,
    score,
  };
};

const compareDbqAiFieldMatches = (left, right) => {
  if (right.score !== left.score) return right.score - left.score;
  if ((left.sourceOrder ?? Infinity) !== (right.sourceOrder ?? Infinity)) {
    return (left.sourceOrder ?? Infinity) - (right.sourceOrder ?? Infinity);
  }
  return String(left.fieldId || '').localeCompare(String(right.fieldId || ''));
};

const getBestSingleAiTargetFieldIds = ({ fieldContexts = [], ...options } = {}) => {
  const matches = fieldContexts
    .map((context) => scoreDbqAiFieldContext({ context, ...options }))
    .filter(Boolean)
    .sort(compareDbqAiFieldMatches);

  if (matches.length === 0) return [];
  const bestScore = matches[0].score;
  return matches
    .filter((match) => match.score === bestScore)
    .map((match) => match.fieldId);
};

const extractDiagnosticQuestionStem = (value) => {
  const normalized = normalizeAiFieldMatchText(value);
  const match = normalized.match(/(?:^|\s)(\d+[a-z])(?:\s|$)/);
  return match ? match[1] : null;
};

const isDiagnosticTestingSectionContext = (context = {}) =>
  context.search?.sectionLabel?.includes('diagnostic') ||
  context.search?.sectionId?.includes('diagnostic');

const isDiagnosticOtherFindingsPromptContext = (context = {}) => {
  if (context.fieldType !== 'yes_no') return false;
  if (!isDiagnosticTestingSectionContext(context)) return false;
  return aiMatchIncludesAnyTermSet(context.search.fieldLabel, [
    ['other', 'diagnostic', 'test', 'findings'],
    ['other', 'diagnostic', 'test', 'findings', 'results'],
    ['other', 'significant', 'diagnostic', 'test', 'findings'],
    ['other', 'clinically', 'relevant', 'diagnostic', 'test', 'findings'],
  ]);
};

const isDiagnosticOtherFindingsDetailContext = (context = {}) => {
  if (!isDiagnosticTestingSectionContext(context)) return false;
  if (!AI_FIELD_TYPE_SETS.text.has(context.fieldType)) return false;

  const label = context.search?.fieldLabel || '';
  const fieldId = context.search?.fieldId || '';
  if (['date', 'results', 'result', 'specify', 'type of test', 'type of study procedure'].includes(label)) {
    return false;
  }
  if (
    aiMatchIncludesAnyTermSet(label, [
      ['other', 'symptom'],
      ['other', 'factor'],
      ['other', 'movement'],
      ['reason'],
      ['abnormal', 'findings'],
    ])
  ) {
    return false;
  }
  if (aiMatchIncludesAnyTermSet(fieldId, [['specify'], ['reason']])) {
    return false;
  }

  return true;
};

const scoreDiagnosticOtherFindingsDetail = ({ context, prompt }) => {
  if (!context || !prompt) return null;
  if (!isDiagnosticOtherFindingsDetailContext(context)) return null;
  if (context.sectionId !== prompt.sectionId) return null;
  if ((context.sourceOrder ?? -1) <= (prompt.sourceOrder ?? -1)) return null;

  let score = 0;
  const promptStem = extractDiagnosticQuestionStem(prompt.fieldId);
  const contextStem = extractDiagnosticQuestionStem(context.fieldId);

  if (promptStem && contextStem && promptStem === contextStem) score += 400;
  if (prompt.search.fieldLabel && context.search.breadcrumbs.includes(prompt.search.fieldLabel)) {
    score += 200;
  }

  const distance = (context.sourceOrder ?? Infinity) - (prompt.sourceOrder ?? -Infinity);
  if (Number.isFinite(distance) && distance > 0) {
    score += Math.max(120 - distance, 0);
  }

  if (
    aiMatchIncludesAnyTermSet(context.search.fieldLabel, [
      ['type', 'test', 'procedure', 'date', 'results'],
      ['brief', 'summary'],
    ])
  ) {
    score += 160;
  }

  if (
    aiMatchIncludesAnyTermSet(context.search.fieldId, [
      ['detail'],
      ['details'],
      ['summary'],
      ['description'],
      ['result'],
      ['results'],
    ])
  ) {
    score += 90;
  }

  if (context.fieldType === 'long_text') score += 40;
  if (score <= 0) return null;

  return {
    ...context,
    score,
  };
};

const getDiagnosticOtherFindingsAiTargetFieldIds = (fieldContexts = []) => {
  const prompts = fieldContexts.filter(isDiagnosticOtherFindingsPromptContext);
  const matches = prompts
    .flatMap((prompt) =>
      fieldContexts
        .map((context) => scoreDiagnosticOtherFindingsDetail({ context, prompt }))
        .filter(Boolean))
    .sort(compareDbqAiFieldMatches);

  if (matches.length === 0) return [];
  return [...new Set(matches.map((match) => match.fieldId))];
};

const getMedicalHistoryFallbackAiTargetFieldIds = (fieldContexts = []) =>
  fieldContexts
    .filter((context) => {
      const sectionText = `${context.search.sectionLabel} ${context.search.sectionId}`;
      return aiMatchIncludesAnyTermSet(sectionText, [
        ['medical', 'history'],
        ['clinical', 'findings'],
        ['history', 'muscle', 'injury'],
      ]);
    })
    .map((context) => context.fieldId);

const getFallbackAiGenerationTargetFieldIds = ({
  draftType,
  formJson = {},
} = {}) => {
  const fieldContexts = collectDbqAiFieldContexts(formJson);
  if (fieldContexts.length === 0) return [];

  switch (draftType) {
    case 'medical_history':
      return getMedicalHistoryFallbackAiTargetFieldIds(fieldContexts);
    case 'evidence_review':
      return getBestSingleAiTargetFieldIds({
        fieldContexts,
        allowedFieldTypes: AI_FIELD_TYPE_SETS.text,
        patterns: [
          ['evidence', 'review'],
          ['evidence', 'reviewed'],
          ['identify', 'evidence', 'reviewed'],
          ['reviewed', 'records'],
        ],
      });
    case 'functional_impact':
      return getBestSingleAiTargetFieldIds({
        fieldContexts,
        allowedFieldTypes: AI_FIELD_TYPE_SETS.text,
        patterns: [
          ['functional', 'impact'],
          ['impact', 'ability', 'perform'],
          ['occupational', 'task'],
          ['functional', 'impact', 'condition'],
        ],
        excludedPatterns: [
          ['functional', 'loss'],
          ['functional', 'impairment'],
        ],
      });
    case 'diagnostic_testing_results':
      return getBestSingleAiTargetFieldIds({
        fieldContexts,
        allowedFieldTypes: AI_FIELD_TYPE_SETS.longText,
        patterns: [
          ['abnormal', 'findings', 'relationship'],
          ['relationship', 'abnormal', 'findings'],
          ['abnormal', 'results', 'relationship'],
          ['diagnosed', 'condition', 'abnormal', 'findings'],
        ],
        excludedPatterns: [
          ['other', 'significant'],
          ['brief', 'summary'],
        ],
      });
    case 'diagnostic_testing_other_findings':
      return getDiagnosticOtherFindingsAiTargetFieldIds(fieldContexts);
    case 'musculoskeletal_flareups':
      return fieldContexts
        .map((context) => scoreDbqAiFieldContext({
          context,
          allowedFieldTypes: AI_FIELD_TYPE_SETS.musculoskeletal,
          patterns: [
            ['flare', 'ups'],
            ['during', 'flare', 'up'],
            ['description', 'flare', 'ups'],
          ],
        }))
        .filter(Boolean)
        .sort(compareDbqAiFieldMatches)
        .map((match) => match.fieldId);
    case 'musculoskeletal_functional_loss':
      return fieldContexts
        .map((context) => scoreDbqAiFieldContext({
          context,
          allowedFieldTypes: AI_FIELD_TYPE_SETS.musculoskeletal,
          patterns: [
            ['functional', 'loss'],
            ['functional', 'impairment'],
            ['own', 'words', 'functional', 'loss'],
          ],
          excludedPatterns: [
            ['amputation', 'prosthesis'],
          ],
        }))
        .filter(Boolean)
        .sort(compareDbqAiFieldMatches)
        .map((match) => match.fieldId);
    default:
      return [];
  }
};

const getAiGenerationNoticeTargetFieldIds = ({
  state = {},
  draftType,
  formJson = {},
} = {}) => {
  const explicitFieldIds = [
    state?.resolvedFieldId,
    ...(Array.isArray(state?.targetFieldIds) ? state.targetFieldIds : []),
  ]
    .map((fieldId) => String(fieldId || '').trim())
    .filter(Boolean);

  const fieldIds = explicitFieldIds.length > 0
    ? explicitFieldIds
    : getFallbackAiGenerationTargetFieldIds({ draftType, formJson });

  return [...new Set(fieldIds)];
};

const flattenVisibleIfConditions = (visibleIf, collector = { conditions: [], isComplex: false }) => {
  if (!visibleIf || typeof visibleIf !== 'object') {
    return collector;
  }

  if (Array.isArray(visibleIf.all)) {
    for (const condition of visibleIf.all) {
      if (condition?.all || condition?.any) {
        flattenVisibleIfConditions(condition, collector);
      } else if (condition && typeof condition === 'object') {
        collector.conditions.push(condition);
      }
    }
  }

  if (Array.isArray(visibleIf.any)) {
    if (visibleIf.any.length > 1) {
      collector.isComplex = true;
    }

    for (const condition of visibleIf.any) {
      if (condition?.all || condition?.any) {
        flattenVisibleIfConditions(condition, collector);
      } else if (condition && typeof condition === 'object') {
        collector.conditions.push(condition);
      }
    }
  }

  return collector;
};

const doesParentOptionSelectionRevealField = ({ parentField, optionId, fieldValue }) => {
  const fieldType = normalizeDbqFieldType(parentField);

  if (!optionId) return true;

  if (fieldType === 'yes_no') {
    if (optionId === 'yes') return fieldValue === true;
    if (optionId === 'no') return fieldValue === false;
    return false;
  }

  if (fieldType === 'multi_select') {
    return Array.isArray(fieldValue) && fieldValue.includes(optionId);
  }

  if (fieldType === 'checkbox') {
    return fieldValue === true;
  }

  return fieldValue === optionId;
};

const isInlineSuggestionCandidate = (suggestion = {}) =>
  suggestion &&
  typeof suggestion === 'object' &&
  suggestion.status === SUGGESTION_STATUS_FILTERS.pending &&
  suggestion.source !== 'ai_notice' &&
  !suggestion.aiNotice;

const isInlineAiNotice = (suggestion = {}) =>
  suggestion &&
  typeof suggestion === 'object' &&
  suggestion.status === SUGGESTION_STATUS_FILTERS.pending &&
  (suggestion.source === 'ai_notice' || suggestion.aiNotice);

const isAiGeneratedInlineSuggestion = (suggestion = {}) =>
  Boolean(suggestion && typeof suggestion === 'object' && (
    suggestion.source === 'ai_generated' ||
    (suggestion.aiDraft && typeof suggestion.aiDraft === 'object' && !Array.isArray(suggestion.aiDraft))
  ));

const getInlineSuggestionEvidenceRefs = (suggestion = null) =>
  (Array.isArray(suggestion?.aiDraft?.sourceEvidenceRefs)
    ? suggestion.aiDraft.sourceEvidenceRefs
    : [])
    .map((ref) => {
      const pages = (Array.isArray(ref?.topPages) ? ref.topPages : [])
        .map((page) => Number(page?.pageNumber))
        .filter((pageNumber) => Number.isInteger(pageNumber) && pageNumber > 0);

      return {
        fileId: String(ref?.fileId || '').trim(),
        fileName: String(ref?.fileName || '').trim() || 'Evidence file',
        pages,
      };
    })
    .filter((ref) => ref.fileId && ref.pages.length > 0);

const getInlineSuggestionTranscriptRefs = (suggestion = null) =>
  Array.isArray(suggestion?.aiDraft?.transcriptRefs) ? suggestion.aiDraft.transcriptRefs : [];

const isInlineGenerationFailureNotice = (suggestion = null) => {
  if (!isInlineAiNotice(suggestion)) return false;
  const tone = String(suggestion?.aiNotice?.tone || '').trim().toLowerCase();
  const title = String(suggestion?.aiNotice?.title || suggestion?.summary || '').trim().toLowerCase();
  return tone === 'warning' && title.includes('unable to be produced');
};

const isInlineProgressNotice = (suggestion = null) => {
  if (!isInlineAiNotice(suggestion)) return false;
  const tone = String(suggestion?.aiNotice?.tone || '').trim().toLowerCase();
  return tone === 'progress';
};

const getInlineGenerationFailureMessage = (suggestion = null) => {
  const message = String(
    suggestion?.note ||
    suggestion?.aiNotice?.message ||
    suggestion?.payload?.value ||
    suggestion?.summary ||
    'AI was unable to produce a response for this field.',
  ).trim();
  return message || 'AI was unable to produce a response for this field.';
};

const buildMissingDetailEvidencePatch = (result = {}) => ({
  sourceEvidenceRefs: Array.isArray(result?.sourceEvidenceRefs) ? result.sourceEvidenceRefs : [],
  transcriptRefs: Array.isArray(result?.transcriptRefs) ? result.transcriptRefs : [],
});

const mergeMissingDetailEvidencePatch = (currentPatch = {}, nextPatch = {}) => ({
  sourceEvidenceRefs: [
    ...(Array.isArray(currentPatch?.sourceEvidenceRefs) ? currentPatch.sourceEvidenceRefs : []),
    ...(Array.isArray(nextPatch?.sourceEvidenceRefs) ? nextPatch.sourceEvidenceRefs : []),
  ],
  transcriptRefs: [
    ...(Array.isArray(currentPatch?.transcriptRefs) ? currentPatch.transcriptRefs : []),
    ...(Array.isArray(nextPatch?.transcriptRefs) ? nextPatch.transcriptRefs : []),
  ],
});

const hasMissingDetailEvidencePatch = (patch = {}) =>
  (Array.isArray(patch?.sourceEvidenceRefs) && patch.sourceEvidenceRefs.length > 0) ||
  (Array.isArray(patch?.transcriptRefs) && patch.transcriptRefs.length > 0);

const getInlineSuggestionActionSource = ({
  latestPendingSuggestion = null,
  latestAcceptedSuggestion = null,
} = {}) =>
  latestPendingSuggestion || latestAcceptedSuggestion || null;

const getSuggestionTimestampValue = (suggestion = {}) => {
  const timestamp = suggestion?.updatedAt || suggestion?.createdAt || null;
  const value = timestamp ? new Date(timestamp).getTime() : Number.NaN;
  return Number.isFinite(value) ? value : 0;
};

const getLatestPendingInlineSuggestion = (suggestions = []) =>
  (Array.isArray(suggestions) ? suggestions : [])
    .filter(isInlineSuggestionCandidate)
    .sort((left, right) => getSuggestionTimestampValue(right) - getSuggestionTimestampValue(left))[0] || null;

const getLatestInlineSuggestionWithStatus = (suggestions = [], status) =>
  (Array.isArray(suggestions) ? suggestions : [])
    .filter((suggestion) => String(suggestion?.status || '').toLowerCase() === status)
    .sort((left, right) => getSuggestionTimestampValue(right) - getSuggestionTimestampValue(left))[0] || null;

const getInlineSuggestionsWithStatus = (suggestions = [], status) =>
  (Array.isArray(suggestions) ? suggestions : [])
    .filter((suggestion) => String(suggestion?.status || '').toLowerCase() === status)
    .sort((left, right) => getSuggestionTimestampValue(right) - getSuggestionTimestampValue(left));

const getLatestInlineAiNotice = (suggestions = []) =>
  (Array.isArray(suggestions) ? suggestions : [])
    .filter(isInlineAiNotice)
    .sort((left, right) => getSuggestionTimestampValue(right) - getSuggestionTimestampValue(left))[0] || null;

const getInlineSuggestionValue = (suggestion = null) => {
  if (!suggestion || typeof suggestion !== 'object') return undefined;
  if (typeof suggestion.appliedValue !== 'undefined') return suggestion?.appliedValue;
  return suggestion?.payload?.value;
};

const formatInlineSuggestionDateTime = (dateString) => {
  if (!dateString) return 'Unknown date';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const getInlineSuggestionReviewerName = (suggestion = null) =>
  String(
    suggestion?.rejectedBy?.name ||
    suggestion?.rejectedBy?.email ||
    suggestion?.rejectedBy?.userId ||
    'Unknown reviewer',
  ).trim();

const getInlineSuggestionDisplayText = (suggestion = null) => {
  const value = getInlineSuggestionValue(suggestion);
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value !== 'undefined' && value !== null) return String(value);
  return String(suggestion?.summary || '').trim();
};

const inlineSuggestionHasMissingDetail = (suggestion = null) => {
  if (!suggestion || typeof suggestion !== 'object') return false;
  return String(suggestion?.payload?.value || suggestion?.summary || '').includes(MISSING_USER_INPUT_TOKEN);
};

const getInlineSuggestionMissingInputs = (suggestion = null) => {
  const configuredInputs = Array.isArray(suggestion?.aiDraft?.missingUserInputs)
    ? suggestion.aiDraft.missingUserInputs
    : [];
  if (configuredInputs.length > 0) return configuredInputs;

  return inlineSuggestionHasMissingDetail(suggestion)
    ? [{
      key: 'missing_detail',
      label: 'Missing detail',
      token: MISSING_USER_INPUT_TOKEN,
      placeholder: 'Enter the missing detail',
    }]
    : [];
};

const buildInlineMissingInputStateKey = (missingInput, index) =>
  String(missingInput?.key || missingInput?.label || index);

const replaceInlineMissingInputOccurrence = (text, token, occurrenceIndex, replacement) => {
  const normalizedText = String(text || '');
  const normalizedToken = String(token || '');
  if (!normalizedToken) return normalizedText;
  if (!Number.isInteger(occurrenceIndex) || occurrenceIndex < 0) {
    return normalizedText.split(normalizedToken).join(replacement);
  }

  let nextText = '';
  let searchStart = 0;
  let currentOccurrence = 0;

  while (searchStart < normalizedText.length) {
    const tokenIndex = normalizedText.indexOf(normalizedToken, searchStart);
    if (tokenIndex === -1) {
      nextText += normalizedText.slice(searchStart);
      break;
    }

    nextText += normalizedText.slice(searchStart, tokenIndex);
    nextText += currentOccurrence === occurrenceIndex ? replacement : normalizedToken;
    searchStart = tokenIndex + normalizedToken.length;
    currentOccurrence += 1;
  }

  return nextText;
};

const buildInlineSuggestionDraftsFromFormData = (sourceFormData = {}) => {
  const suggestionsByField = sourceFormData?._suggestions;
  if (!suggestionsByField || typeof suggestionsByField !== 'object' || Array.isArray(suggestionsByField)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(suggestionsByField)
      .map(([fieldId, suggestions]) => {
        const latestSuggestion = getLatestPendingInlineSuggestion(suggestions);
        if (!latestSuggestion) return null;
        return [fieldId, latestSuggestion?.payload?.value];
      })
      .filter(Boolean),
  );
};

const hasOwnValue = (object, key) =>
  Boolean(object && Object.prototype.hasOwnProperty.call(object, key));

const isDbqValuePresent = (value) => {
  if (typeof value === 'boolean') return true;
  if (Array.isArray(value)) return value.length > 0;
  if (value === null || typeof value === 'undefined') return false;
  return String(value).trim() !== '';
};

const evaluateInlineCondition = (conditionGroup, getValue, getRepeatCount = () => 0) => {
  if (!conditionGroup) return false;

  const checkSingle = (condition) => {
    if (condition.operator?.startsWith('repeat_count_')) {
      const count = getRepeatCount(condition.field_id);
      const target = Number(condition.value);
      switch (condition.operator) {
        case 'repeat_count_equals': return count === target;
        case 'repeat_count_gt': return count > target;
        case 'repeat_count_gte': return count >= target;
        case 'repeat_count_lt': return count < target;
        case 'repeat_count_lte': return count <= target;
        default: return true;
      }
    }

    const value = getValue(condition.field_id);
    switch (condition.operator) {
      case 'equals': return value === condition.value;
      case 'not_equals': return value !== condition.value;
      case 'contains': return Array.isArray(value) && value.includes(condition.value);
      case 'not_contains': return !Array.isArray(value) || !value.includes(condition.value);
      case 'exists': return value !== undefined && value !== null && value !== '';
      case 'not_exists': return value === undefined || value === null || value === '';
      case 'in': return Array.isArray(condition.value) && condition.value.includes(value);
      case 'not_in': return !Array.isArray(condition.value) || !condition.value.includes(value);
      case 'gt': return Number(value) > Number(condition.value);
      case 'gte': return Number(value) >= Number(condition.value);
      case 'lt': return Number(value) < Number(condition.value);
      case 'lte': return Number(value) <= Number(condition.value);
      default: return true;
    }
  };

  const evaluateGroup = (group) => {
    if (!group) return true;
    const allPass = !group.all || group.all.every((condition) =>
      (condition.all || condition.any) ? evaluateGroup(condition) : checkSingle(condition));
    const anyPass = !group.any || group.any.some((condition) =>
      (condition.all || condition.any) ? evaluateGroup(condition) : checkSingle(condition));
    return allPass && anyPass;
  };

  return evaluateGroup(conditionGroup);
};

const cloneMusculoskeletalSupplementalData = (data) => {
  const normalized = normalizeMusculoskeletalSupplementalData(data);
  return {
    ...normalized,
    functionalLosses: Object.fromEntries(
      Object.entries(normalized.functionalLosses || {}).map(([key, value]) => [
        key,
        value && typeof value === 'object' && !Array.isArray(value) ? { ...value } : value,
      ]),
    ),
  };
};

const setMusculoskeletalSupplementalFieldValue = (data, fieldId, value) => {
  if (!isMusculoskeletalSupplementalFieldId(fieldId)) {
    return normalizeMusculoskeletalSupplementalData(data);
  }

  const next = cloneMusculoskeletalSupplementalData(data);
  const segments = String(fieldId).trim().split('.').slice(2);
  if (segments.length === 0) return next;

  let cursor = next;
  for (let index = 0; index < segments.length - 1; index += 1) {
    const segment = segments[index];
    if (!cursor[segment] || typeof cursor[segment] !== 'object' || Array.isArray(cursor[segment])) {
      cursor[segment] = {};
    } else {
      cursor[segment] = { ...cursor[segment] };
    }
    cursor = cursor[segment];
  }

  cursor[segments[segments.length - 1]] = value;
  return normalizeMusculoskeletalSupplementalData(next);
};

const areInlineSuggestionValuesEqual = (left, right) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null);

const buildInlineMusculoskeletalSupplementalData = (sourceData, getInlineValue) => {
  if (typeof getInlineValue !== 'function') {
    return normalizeMusculoskeletalSupplementalData(sourceData);
  }

  return Object.keys(MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_DEFINITIONS).reduce(
    (nextData, fieldId) => {
      const value = getInlineValue(fieldId, []);
      return typeof value === 'undefined'
        ? nextData
        : setMusculoskeletalSupplementalFieldValue(nextData, fieldId, value);
    },
    normalizeMusculoskeletalSupplementalData(sourceData),
  );
};

const getChangedMusculoskeletalSupplementalFieldIds = (previousData, nextData) =>
  Object.keys(MUSCULOSKELETAL_SUPPLEMENTAL_FIELD_DEFINITIONS).filter((fieldId) => {
    const previousValue = getMusculoskeletalSupplementalFieldValue(previousData, fieldId);
    const nextValue = getMusculoskeletalSupplementalFieldValue(nextData, fieldId);
    return !areInlineSuggestionValuesEqual(previousValue, nextValue);
  });

const areOrderedFieldIdsEqual = (left = [], right = []) =>
  left.length === right.length && left.every((fieldId, index) => fieldId === right[index]);

const orderFieldIdsByRenderedPosition = (fieldIds = [], resolveElement) => {
  if (typeof document === 'undefined') return fieldIds;

  return [...fieldIds]
    .map((fieldId, index) => {
      const element =
        typeof resolveElement === 'function'
          ? resolveElement(fieldId)
          : null;
      if (!element) {
        return {
          fieldId,
          index,
          isRendered: false,
          top: Number.MAX_SAFE_INTEGER,
          left: Number.MAX_SAFE_INTEGER,
        };
      }

      const rect = element.getBoundingClientRect();
      return {
        fieldId,
        index,
        isRendered: true,
        top: rect.top + window.scrollY,
        left: rect.left + window.scrollX,
      };
    })
    .sort((left, right) => {
      if (left.isRendered !== right.isRendered) {
        return left.isRendered ? -1 : 1;
      }
      if (left.top !== right.top) return left.top - right.top;
      if (left.left !== right.left) return left.left - right.left;
      return left.index - right.index;
    })
    .map((item) => item.fieldId);
};

export {
  normalizeDbqTextForCompare,
  isExaminerCertificationAndSignatureSection,
  findRemarksFieldIdInElements,
  parseStoredRequestContentions,
  getContentionDisplayLabel,
  getMoToeLabel,
  buildContentionOpinionHeader,
  buildCompiledContentionOpinionText,
  getEditableAutoMedicalOpinionText,
  EMPTY_SUGGESTION_STATS,
  SUGGESTION_STATUS_FILTERS,
  SUGGESTION_STATUS_FILTER_ORDER,
  INLINE_SUGGESTION_VIEW_MODES,
  MISSING_USER_INPUT_TOKEN,
  INLINE_SUGGESTION_NAVIGATION_STATUSES,
  ACTIVE_DBQ_AI_GENERATION_STATUSES,
  isActiveDbqAiGenerationStatus,
  hasActiveDbqAiGeneration,
  getUserDbqAiPollingInterval,
  AI_DRAFT_LABELS,
  buildSuggestionStatsFromItems,
  buildAiReviewNotice,
  normalizeDbqFieldType,
  AI_FIELD_TYPE_SETS,
  normalizeAiFieldMatchText,
  aiMatchIncludesAllTerms,
  aiMatchIncludesAnyTermSet,
  buildAiFieldSearchIndex,
  collectDbqAiFieldContexts,
  scoreDbqAiFieldContext,
  compareDbqAiFieldMatches,
  getBestSingleAiTargetFieldIds,
  extractDiagnosticQuestionStem,
  isDiagnosticTestingSectionContext,
  isDiagnosticOtherFindingsPromptContext,
  isDiagnosticOtherFindingsDetailContext,
  scoreDiagnosticOtherFindingsDetail,
  getDiagnosticOtherFindingsAiTargetFieldIds,
  getMedicalHistoryFallbackAiTargetFieldIds,
  getFallbackAiGenerationTargetFieldIds,
  getAiGenerationNoticeTargetFieldIds,
  flattenVisibleIfConditions,
  doesParentOptionSelectionRevealField,
  isInlineSuggestionCandidate,
  isInlineAiNotice,
  isAiGeneratedInlineSuggestion,
  getInlineSuggestionEvidenceRefs,
  getInlineSuggestionTranscriptRefs,
  isInlineGenerationFailureNotice,
  isInlineProgressNotice,
  getInlineGenerationFailureMessage,
  buildMissingDetailEvidencePatch,
  mergeMissingDetailEvidencePatch,
  hasMissingDetailEvidencePatch,
  getInlineSuggestionActionSource,
  getSuggestionTimestampValue,
  getLatestPendingInlineSuggestion,
  getLatestInlineSuggestionWithStatus,
  getInlineSuggestionsWithStatus,
  getLatestInlineAiNotice,
  getInlineSuggestionValue,
  formatInlineSuggestionDateTime,
  getInlineSuggestionReviewerName,
  getInlineSuggestionDisplayText,
  inlineSuggestionHasMissingDetail,
  getInlineSuggestionMissingInputs,
  buildInlineMissingInputStateKey,
  replaceInlineMissingInputOccurrence,
  buildInlineSuggestionDraftsFromFormData,
  hasOwnValue,
  isDbqValuePresent,
  evaluateInlineCondition,
  cloneMusculoskeletalSupplementalData,
  setMusculoskeletalSupplementalFieldValue,
  areInlineSuggestionValuesEqual,
  buildInlineMusculoskeletalSupplementalData,
  getChangedMusculoskeletalSupplementalFieldIds,
  areOrderedFieldIdsEqual,
  orderFieldIdsByRenderedPosition,
};
